/**
 * Who may speak, show a camera, share a screen, and who has to knock.
 *
 * Four questions, and the first three have the same three answers — so they are
 * one row shape repeated, not four hand-written blocks. `Who` is
 * `'host' | 'everyone' | string[]`, and the third answer is the list: picking
 * "Named people" turns the row into a set of names with checkboxes beside them.
 *
 * The server validates the whole policy object and enforces it on the track
 * prefix before the SFU is called, so this editor is the convenient way to say
 * it and never the thing that makes it true.
 */

import { ds } from '../../ds';
import { useT } from '../../i18n';
import { participantLabel } from '../../lib/relay/types';
import type { MeetingParticipant, MeetingPolicy, Who } from '../../lib/relay/types';

type Lane = 'mic' | 'camera' | 'screen';

const LANES: Lane[] = ['mic', 'camera', 'screen'];

/** The select's value, which is a `Who` flattened to three choices. */
type Choice = 'host' | 'everyone' | 'named';

const choiceOf = (who: Who): Choice => (Array.isArray(who) ? 'named' : who);

export function PolicyEditor({
  policy,
  participants,
  onChange
}: {
  policy: MeetingPolicy;
  participants: MeetingParticipant[];
  onChange: (policy: MeetingPolicy) => void;
}) {
  const { Card, Checkbox, Select } = ds();
  const t = useT();

  const whoOptions = [
    { value: 'host', label: t('call.policy.who.host') },
    { value: 'everyone', label: t('call.policy.who.everyone') },
    { value: 'named', label: t('call.policy.who.named') }
  ];

  const setLane = (lane: Lane, choice: Choice) => {
    const next: Who = choice === 'named' ? [] : choice;
    onChange({ ...policy, [lane]: next });
  };

  const toggleName = (lane: Lane, id: string, on: boolean) => {
    const current = policy[lane];
    const list = Array.isArray(current) ? current : [];
    onChange({ ...policy, [lane]: on ? [...list, id] : list.filter((x) => x !== id) });
  };

  return (
    <Card padding="compact" tone="sunken">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-12)' }}>
        {LANES.map((lane) => {
          const who = policy[lane];
          const named = Array.isArray(who) ? who : null;
          return (
            <div key={lane}>
              <Select
                label={t(`call.policy.${lane}`)}
                value={choiceOf(who)}
                options={whoOptions}
                onChange={(e) => setLane(lane, e.target.value as Choice)}
              />
              {named && (
                <div style={{ paddingTop: 'var(--space-8)', paddingLeft: 'var(--space-4)' }}>
                  {participants.map((p) => (
                    <Checkbox
                      key={p.id}
                      label={participantLabel(p)}
                      checked={named.includes(p.id)}
                      onChange={(on) => toggleName(lane, p.id, on)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <Select
          label={t('call.policy.lobby')}
          value={policy.lobby}
          options={[
            { value: 'strangers', label: t('call.policy.lobby.strangers') },
            { value: 'everyone', label: t('call.policy.lobby.everyone') },
            { value: 'off', label: t('call.policy.lobby.off') }
          ]}
          onChange={(e) =>
            onChange({ ...policy, lobby: e.target.value as MeetingPolicy['lobby'] })
          }
        />
      </div>
    </Card>
  );
}
